import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MainNavbarComponent } from '../main-navbar/main-navbar.component';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { submitSearch } from 'src/app/state/action';
import { getBarColor, getFlagColor, getFlagImage, getStageBgColor, getStageColor } from 'src/app/shared/utils/bracket.utils';

interface IHighlight {
  entityid: string;
  entity_name: string;
  gsubind: string;
  innovation_score: number;
  score_change: number | null;
  signal: 'BUY' | 'HOLD' | 'SELL';
  signal_previous: 'BUY' | 'HOLD' | 'SELL' | null;
  bucket: string | null;
  companytype: string;
  score_bracket?: number | null;
}
interface ITheme {
  cluster_id: number;
  cluster_name: string;
  stage: string;
  invention_count_12m: number;
  relative_growth: number;
}
const CATEGORIES = ['rising_public', 'rising_private'] as const;

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [MainNavbarComponent, CommonModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit {
  highlights: Record<string, IHighlight[]> = {};
  dataDate: string | null = null;
  themes: { top_clusters: ITheme[]; flop_clusters: ITheme[] } = { top_clusters: [], flop_clusters: [] };
  readonly MOVER_CARDS = 5;

  flagImage = getFlagImage;
  flagColor = getFlagColor;
  barColor = getBarColor;
  stageColor = getStageColor;
  stageBgColor = getStageBgColor;

  constructor(private router: Router, private http: HttpClient, private store: Store<AppState>) {}

  ngOnInit(): void {
    const headers = { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') };

    this.http.get<{ payload: Record<string, IHighlight[]> & { date_eom?: string } }>(
      '/backend/innovationMetrics/highlights', { headers },
    ).subscribe(res => {
      if (!res?.payload) return;
      this.highlights = res.payload;
      this.dataDate = res.payload.date_eom ?? null;
    });

    // Same lists as the Technology Trends page, trimmed to 3 + 3 server-side.
    this.http.post<{ payload: { top_clusters: ITheme[]; flop_clusters: ITheme[] } }>(
      '/backend/thematicView/idea-generation/snapshot', { top_flop_only: true }, { headers },
    ).subscribe(res => { if (res?.payload) this.themes = res.payload; });
  }

  get hasHighlights(): boolean {
    return CATEGORIES.some(c => this.highlights[c]?.length);
  }

  get hasThemes(): boolean {
    return this.themes.top_clusters.length > 0 || this.themes.flop_clusters.length > 0;
  }

  get risingPublic(): IHighlight[] {
    return (this.highlights['rising_public'] || []).slice(0, this.MOVER_CARDS);
  }

  get risingPrivate(): IHighlight[] {
    return (this.highlights['rising_private'] || [])
      .filter(h => !/real estate/i.test(h.bucket || ''))
      .slice(0, this.MOVER_CARDS);
  }

  openCompany(c: IHighlight): void {
    this.store.dispatch(submitSearch({ searchParams: { companies: [c] }, searchType: 'company' }));
  }

  openTheme(t: ITheme): void {
    const searchParams = {
      themes: [{
        theme_search_type: 'cluster',
        cluster_id: String(t.cluster_id),
        cluster_name: t.cluster_name,
        api_query: null,
        name: t.cluster_name,
      }],
    };
    this.store.dispatch(submitSearch({ searchParams, searchType: 'theme' }));
  }

  openCompanySearch() { this.router.navigate(['/company-search']); }
  openThematicSearch() { this.router.navigate(['/thematic-search']); }
  openIdeaGeneration() { this.router.navigate(['/idea-generation']); }
  openMonitoring() { this.router.navigate(['/monitoring']); }
}
